import React from "react";
import ReactDOM from "react-dom";
import {
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { useFormik } from "formik";
import * as Yup from "yup";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import Swal from "sweetalert2";
import "assets/css/modal.css";
import SaveIcon from "assets/images/SaveIcon.svg";
import { Switch } from "antd";
import { useAuth } from "hooks/useAuth";
import api from "_api";

const ModalChangePassword = ({ open, onClose }) => {
  const { user } = useAuth();

  // Validation schema
  const validationSchema = Yup.object({
    password: Yup.string()
      .required("โปรดระบุรหัสผ่าน")
      .min(8, "ไม่สามารถน้อยกว่า 8 ตัวอักษร"),
    newPassword: Yup.string()
      .required("กรุณากรอกยืนยันรหัสผ่าน")
      .min(8, "ไม่สามารถน้อยกว่า 8 ตัวอักษร")
      .test(
        "not-equal-to-password", // ชื่อการทดสอบ
        "รหัสผ่านใหม่ต้องไม่เหมือนกับรหัสผ่านเดิม", // ข้อความเมื่อเกิดข้อผิดพลาด
        function (value) {
          const { password } = this.parent; // ดึงค่า password จาก parent
          return value !== password; // เช็คว่า newPassword ไม่เท่ากับ password
        }
      ),
  });

  const productFields = [
    {
      label: "รหัสผ่านเดิม",
      name: "password",
      type: "password",
      required: true,
    },
    {
      label: "รหัสผ่านใหม่",
      name: "newPassword",
      type: "password",
      required: true,
    },
  ];

  const errorType = (status) => {
    if (status == 400) {
      return {
        title: "กรุณาใส่ข้อมูลให้ครบถ้วน",
        text: "กรุณาใส่รหัสผ่านให้ครบถ้วน",
      };
    } else if (status == 401) {
      return {
        title: "รหัสผ่านผิด",
        text: "กรุณาใส่รหัสผ่านให้ถูกต้อง",
      };
    } else {
      return { title: "มีบางอย่างผิดพลาดกรุณาแจ้งซัพพอร์ต", text: null };
    }
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
    },
    validationSchema: validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const userResponse = await api.super_admin.ChangePassword(
          user.id,
          values
        );
        if (userResponse.isStatus) {
          resetForm();
          onClose();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: errorType(error.status).title,
          text: errorType(error.status).text,
          timer: 3000,
          showConfirmButton: false,
          customClass: {
            container: "swal2-container-custom",
          },
          didOpen: () => {
            document.querySelector(
              ".swal2-container-custom"
            ).style.zIndex = 1500;
          },
        });
      }
    },
  });

  const handleCancel = () => {
    const formHasUnsavedChanges = Object.keys(touched).some(
      (field) => touched[field]
    );

    if (formHasUnsavedChanges) {
      Swal.fire({
        html: `
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <!-- Left side icon/picture -->
            <div style="margin-right: 20px;">
              <img src="${SaveIcon}" alt="warning icon" width="300" height="300"/>
            </div>
      
            <!-- Right side text and buttons -->
            <div style="flex: 1;">
              <h2 style="font-size: 24px; color: #333;">ยืนยันการยกเลิกหรือไม่</h2>
              <div id="custom-buttons" style="display: flex; justify-content: flex-end; margin-top: 20px;">
                <!-- React buttons will be injected here -->
              </div>
            </div>
          </div>
        `,
        showCancelButton: false, // Disable default buttons
        showConfirmButton: false, // Disable default buttons
        customClass: {
          container: "swal2-container-custom",
        },
        didOpen: () => {
          document.querySelector(".swal2-container-custom").style.zIndex = 1500;

          // Render your React button components after the Swal modal opens
          ReactDOM.render(
            <>
              <GradientButton
                onClick={() => {
                  // Handle cancel behavior
                  Swal.close(); // Close Swal
                }}
                style={{ width: "100%", marginBottom: "10px" }} // Make full width
              >
                ยกเลิก
              </GradientButton>
              <GradientButtonOutline
                onClick={() => {
                  // Close the current Swal first before showing the confirmation Swal
                  Swal.close();
                  formik.resetForm();
                  onClose(); // Close the modal
                }}
                style={{ width: "100%" }}
              >
                ตกลง
              </GradientButtonOutline>
            </>,
            document.getElementById("custom-buttons") // Inject React components into this div
          );
        },
      });
    } else {
      formik.resetForm();
      onClose();
    }
  };

  const renderFields = (fields) => {
    return fields.map((field, index) => (
      <React.Fragment key={index}>
        <Grid item xs={12} sm={2}>
          <Typography variant="subtitle1">
            {field.label}
            {field.required && (
              <span style={{ marginLeft: "4px", color: "red" }}>*</span>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={10}>
          {field.type === "checkbox" ? (
            <Switch
              value={formik.values.status}
              onChange={(checked) => {
                if (!field.disabled) {
                  formik.setFieldValue("status", checked);
                }
              }}
            />
          ) : (
            <TextField
              disabled={field.disabled}
              id={field.name}
              name={field.name}
              type={field.type}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              {...getFieldProps(field.name)}
              error={
                formik.touched[field.name] && Boolean(formik.errors[field.name])
              }
              helperText={
                formik.touched[field.name] && formik.errors[field.name]
              }
              fullWidth
              InputProps={{
                sx: { height: "40px" },
                inputProps: {
                  style: {
                    "-moz-appearance": "textfield", // Hide up/down arrows in Firefox
                    "-webkit-appearance": "none", // Hide up/down arrows in Chrome/Safari
                    appearance: "none", // Hide up/down arrows in other browsers
                  },
                  // maxLength:
                  //   field.name === "amphure_id"
                  //     ? 13
                  //     : field.name === "district_id"
                  //     ? 10
                  //     : undefined, // Set max length
                  inputMode: field.type === "text" ? "numeric" : undefined, // Ensure numeric keyboard on mobile
                  pattern: field.type === "text" ? "[0-9]*" : undefined, // Restrict input to numbers
                },
              }}
            />
          )}
        </Grid>
      </React.Fragment>
    ));
  };

  const { errors, touched, getFieldProps, handleSubmit } = formik;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      sx={{
        borderRadius: "8px",
      }}
    >
      <DialogTitle>
        <Typography variant="h4">เปลี่ยนรหัสผ่าน</Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3} sx={{ padding: 3 }}>
            <Grid item xs={12}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderFields(productFields)}
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <GradientButtonOutline
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={handleCancel}
        >
          ยกเลิก
        </GradientButtonOutline>
        <GradientButton
          variant="contained"
          onClick={formik.handleSubmit}
          type="submit"
        >
          บันทึก
        </GradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default ModalChangePassword;
